import { ApiResponse } from '@models/common/api';
import { IPostAnalytics } from '@models/social/analytics';
import {
  IDeleteCommentResponse,
  IGetCommentsResponse,
  IPostCommentResponse,
  IReplyToACommentResponse,
} from '@models/social/comments';
import {
  ICreatePost,
  IChangeDate,
  ICreateLocationBrandRequest,
  IfindHashtags,
  IFindPostsWithPaginationRequest,
  IPEDCreateRequest,
  ISaveDraftRequest,
} from '@models/social/request';
import {
  IPost,
  IFindOneDraft,
  IFindOnePost,
  IFindPosts,
  IBestPostByScoreItem,
  IOverviewAnalyticsResponse,
  IPEDCreateResponse,
  IFollowersHistoryResponse,
  ICreateLocationBrandResponse,
} from '@models/social/response';
import { ESocialPlatforms } from '@models/social/social';
import _ from 'lodash';

import { createBrandCrudApi } from './brand-crud';
import { del, get, post, put } from './methods';

const BASE_URL = '';

export const socialAPI = {
  posts: {
    findHashtags: async (body: IfindHashtags) => {
      const response = await post<ApiResponse<IfindHashtags>>(BASE_URL + '/posts/hashtags', body);
      return response.data;
    },
    create: async (body: ICreatePost, brandId: number, organizationId: number) => {
      const response = await post<ApiResponse<IPost>>(
        BASE_URL + '/posts',
        body,
        { params: { brandId, organizationId } },
        2,
      );
      return response.data;
    },

    findOne: async (brandId: number, organizationId: number, postId: number) => {
      const response = await get<ApiResponse<IPost>>(BASE_URL + `/posts/${postId}`, {
        params: { brandId, organizationId },
      });
      return response.data;
    },

    findAll: async (brandId: number, organizationId: number) => {
      const response = await get<ApiResponse<IFindPosts>>(BASE_URL + '/posts', { params: { brandId, organizationId } });
      return response.data;
    },

    findWithPagination: async (params: IFindPostsWithPaginationRequest) => {
      // No per-call serializer: the shared instance already encodes arrays as repeated parameters
      // (`type=post&type=reel`), which is OpenAPI's default for a query parameter — style `form`
      // with `explode: true` — and what the backend spec advertises. This call used to override it
      // with `indexes: true` to send `type[0]=post`, a shape that spec has no entry for.
      const response = await get<IFindPosts>(BASE_URL + '/posts', { params: { ...params } });
      return response.data;
    },

    delete: async (brandId: number, organizationId: number, postId: number, platforms: ESocialPlatforms[]) => {
      const response = await del<ApiResponse<IFindOnePost>>(BASE_URL + `/posts/${postId}`, {
        params: { brandId, organizationId, platforms },
        paramsSerializer: {
          indexes: true,
        },
      });
      return response.data;
    },
    deletePublication: async ({
      brandId,
      organizationId,
      postId,
      publicationId,
    }: {
      brandId: number;
      organizationId: number;
      postId: number;
      publicationId: number;
    }) => {
      const response = await del<ApiResponse<IFindOnePost>>(BASE_URL + `/posts/publication/${publicationId}`, {
        params: { brandId, organizationId, postId },
      });
      return response.data;
    },
    changeDate: async (body: IChangeDate, brandId: number, organizationId: number, postId: number) => {
      const response = await put<ApiResponse<IPost>>(BASE_URL + `/posts/${postId}`, body, {
        params: { brandId, organizationId },
      });
      return response.data;
    },

    createDraft: async (body: ISaveDraftRequest, brandId: number, organizationId: number) => {
      const response = await post<ApiResponse<IPost>>(BASE_URL + '/posts/drafts', body, {
        params: { brandId, organizationId },
      });
      return response.data;
    },
    updateDraft: async (draftId: number, body: ISaveDraftRequest, brandId: number, organizationId: number) => {
      const response = await put<ApiResponse<IPost>>(BASE_URL + `/posts/drafts/${draftId}`, body, {
        params: { brandId, organizationId },
      });
      return response.data;
    },
    deleteDraft: async (draftId: number, brandId: number, organizationId: number) => {
      const response = await del<ApiResponse<IPost>>(BASE_URL + `/posts/drafts/${draftId}`, {
        params: { brandId, organizationId },
      });
      return response.data;
    },
    findOneDraft: async (draftId: number, brandId: number, organizationId: number) => {
      const response = await get<ApiResponse<IFindOneDraft>>(BASE_URL + `/posts/drafts/${draftId}`, {
        params: { brandId, organizationId },
      });
      return response.data;
    },
    refetchMedia: async (postId: number, brandId: number, organizationId: number, publicationId?: number) => {
      const params: { brandId: number; organizationId: number; publicationId?: number } = { brandId, organizationId };
      if (publicationId) params.publicationId = publicationId;
      const response = await get<ApiResponse<{ mediaUrl: string }>>(BASE_URL + `/posts/${postId}/media`, { params });
      return response.data;
    },
    createPED: async (body: IPEDCreateRequest, brandId: number, organizationId: number) => {
      const response = await post<ApiResponse<IPEDCreateResponse>>(BASE_URL + '/posts/ped', body, {
        params: { brandId, organizationId },
      });
      return response.data;
    },
  },
  brands: {
    ...createBrandCrudApi('/brands'),
    triggerFetch: async (brandId: number, organizationId: number) => {
      const response = await get<ApiResponse<ESocialPlatforms[]>>(BASE_URL + '/brands/trigger-sync', {
        params: { brandId, organizationId },
      });
      return response.data;
    },
    createLocation: async (body: ICreateLocationBrandRequest) => {
      const response = await post<ICreateLocationBrandResponse>(BASE_URL + `/spartacus/brands`, body);
      return response.data;
    },
  },
  analytics: {
    findOverview: async (brandId: number, organizationId: number) => {
      const response = await get<ApiResponse<IOverviewAnalyticsResponse>>(BASE_URL + `/brands/${brandId}/analytics`, {
        params: { organizationId },
      });
      return response.data;
    },
    followersHistory: async (brandId: number, organizationId: number) => {
      const response = await get<ApiResponse<IFollowersHistoryResponse[]>>(
        BASE_URL + `/brands/${brandId}/followers-history`,
        {
          params: { organizationId },
        },
      );
      return response.data;
    },
    bestByScore: async (brandId: number, organizationId: number, limit?: number) => {
      const response = await get<ApiResponse<IBestPostByScoreItem[]>>(BASE_URL + `/posts/best-by-score`, {
        params: { organizationId, brandId, ...(limit ? { limit } : {}) },
      });
      return response.data;
    },
    postOverview: async (brandId: number, organizationId: number, postId: number, platform: ESocialPlatforms) => {
      const ALLOWED_PLATFORMS = new Set([
        'bluesky',
        'facebook',
        'instagram',
        'gmb',
        'linkedin',
        'pinterest',
        'reddit',
        'threads',
        'tiktok',
        'twitter',
        'youtube',
      ]);
      if (!ALLOWED_PLATFORMS.has(platform)) {
        throw new Error(`Invalid platform: ${platform}. Must be one of: ${Array.from(ALLOWED_PLATFORMS).join(', ')}`);
      }
      const response = await get<ApiResponse<IPostAnalytics>>(BASE_URL + `/posts/${postId}/analytics`, {
        params: { organizationId, brandId, platform },
      });
      return response.data;
    },
  },
  comments: {
    getComments: async (brandId: number, organizationId: number, publicationId: number) => {
      const response = await get<ApiResponse<IGetCommentsResponse>>(BASE_URL + `/comments`, {
        params: { brandId, organizationId, publicationId },
      });
      return response.data;
    },
    postComment: async (
      brandId: number,
      organizationId: number,
      body: { publicationId: number; comment: string; mediaUrls?: string[] },
    ) => {
      const response = await post<ApiResponse<IPostCommentResponse>>(BASE_URL + `/comments`, body, {
        params: { brandId, organizationId },
      });
      return response.data;
    },
    deleteComment: async (
      brandId: number,
      organizationId: number,
      body: { commentId: string; publicationId: number },
    ) => {
      const response = await del<ApiResponse<IDeleteCommentResponse>>(BASE_URL + `/comments`, {
        params: { brandId, organizationId },
        data: body,
      });
      return response.data;
    },
    replyToAComment: async (
      brandId: number,
      organizationId: number,
      body: { publicationId: number; commentId: string; comment: string; mediaUrls?: string[]; videoId?: string },
    ) => {
      const response = await post<ApiResponse<IReplyToACommentResponse>>(BASE_URL + `/comments/reply`, body, {
        params: { brandId, organizationId },
      });
      return response.data;
    },
  },
  upload: async (body: FormData) => {
    const response = await post<ApiResponse<string[]>>(`${BASE_URL}/file-storage/upload/social-posting`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};
